// src/hooks/useContactForm.js
import { useState } from 'react';
import emailjs from 'emailjs-com';
import { events } from '../analytics';

// ============================================
// CONFIGURACIÓN DE SEGURIDAD
// ============================================

// Configuración de EmailJS (desde variables de entorno, SIN fallbacks hardcodeados)
const EMAILJS_CONFIG = {
    serviceID: process.env.REACT_APP_EMAILJS_SERVICE_ID,
    templateID: process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
    userID: process.env.REACT_APP_EMAILJS_USER_ID
};

// Límites de longitud para prevenir abuse
const MAX_NAME_LENGTH = 100;
const MAX_EMAIL_LENGTH = 254;
const MAX_MESSAGE_LENGTH = 5000;
const MIN_MESSAGE_LENGTH = 10;

// Rate limiting: máximo 5 envíos por hora
const RATE_LIMIT_KEY = 'contact_form_submissions';
const MAX_SUBMISSIONS = 5;
const RATE_LIMIT_WINDOW = 60 * 60 * 1000; // 1 hora en ms

// ============================================
// FUNCIONES DE SEGURIDAD
// ============================================

/**
 * Verifica que la configuración de EmailJS esté completa
 */
const isConfigValid = () => {
    const { serviceID, templateID, userID } = EMAILJS_CONFIG;
    return Boolean(serviceID && templateID && userID);
};

/**
 * Verifica el rate limit usando localStorage
 * @returns {boolean} true si puede enviar, false si excedió el límite
 */
const checkRateLimit = () => {
    try {
        const submissions = JSON.parse(localStorage.getItem(RATE_LIMIT_KEY) || '[]');
        const now = Date.now();
        const recentSubmissions = submissions.filter(time => now - time < RATE_LIMIT_WINDOW);

        if (recentSubmissions.length >= MAX_SUBMISSIONS) {
            return false;
        }

        recentSubmissions.push(now);
        localStorage.setItem(RATE_LIMIT_KEY, JSON.stringify(recentSubmissions));
        return true;
    } catch {
        // Si localStorage falla, permitir el envío
        return true;
    }
};

/**
 * Validación robusta de email según RFC 5322
 */
const isValidEmail = (email) => {
    if (!email || email.length > MAX_EMAIL_LENGTH) return false;
    const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
    return emailRegex.test(email);
};

/**
 * Sanitiza input removiendo caracteres potencialmente peligrosos
 */
const sanitizeInput = (input, maxLength = MAX_MESSAGE_LENGTH) => {
    if (!input) return '';
    return input
        .trim()
        .replace(/[<>]/g, '') // Prevenir inyección HTML básica
        .substring(0, maxLength);
};

// ============================================
// HOOK PRINCIPAL
// ============================================

export const useContactForm = () => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        message: '',
        privacyAccepted: false,
        storyConsent: false
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState(null);
    const [errors, setErrors] = useState({});

    const validateForm = () => {
        const newErrors = {};
        const name = formData.name.trim();
        const email = formData.email.trim();
        const message = formData.message.trim();

        // Validar nombre
        if (!name) {
            newErrors.name = 'El nombre es requerido';
        } else if (name.length > MAX_NAME_LENGTH) {
            newErrors.name = `El nombre no puede exceder ${MAX_NAME_LENGTH} caracteres`;
        }

        // Validar email
        if (!email) {
            newErrors.email = 'El email es requerido';
        } else if (!isValidEmail(email)) {
            newErrors.email = 'El email no es válido';
        }

        // Validar mensaje
        if (!message) {
            newErrors.message = 'El mensaje es requerido';
        } else if (message.length < MIN_MESSAGE_LENGTH) {
            newErrors.message = `El mensaje debe tener al menos ${MIN_MESSAGE_LENGTH} caracteres`;
        } else if (message.length > MAX_MESSAGE_LENGTH) {
            newErrors.message = `El mensaje no puede exceder ${MAX_MESSAGE_LENGTH} caracteres`;
        }

        // Validar aceptación de política de privacidad
        if (!formData.privacyAccepted) {
            newErrors.privacy = 'Debes aceptar la política de privacidad';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;

        // Manejar checkboxes
        if (type === 'checkbox') {
            setFormData(prev => ({
                ...prev,
                [name]: checked
            }));
            // Limpiar error cuando el usuario marca el checkbox
            if (errors[name] || (name === 'privacyAccepted' && errors.privacy)) {
                setErrors(prev => ({
                    ...prev,
                    [name]: '',
                    privacy: ''
                }));
            }
            return;
        }

        // Aplicar límites de longitud mientras escribe
        let sanitizedValue = value;
        if (name === 'name') {
            sanitizedValue = value.substring(0, MAX_NAME_LENGTH);
        } else if (name === 'email') {
            sanitizedValue = value.substring(0, MAX_EMAIL_LENGTH);
        } else if (name === 'message') {
            sanitizedValue = value.substring(0, MAX_MESSAGE_LENGTH);
        }

        setFormData(prev => ({
            ...prev,
            [name]: sanitizedValue
        }));

        // Limpiar error del campo cuando el usuario empiece a escribir
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validar configuración
        if (!isConfigValid()) {
            setSubmitStatus('config_error');
            return;
        }

        // Validar formulario
        if (!validateForm()) {
            return;
        }

        // Verificar rate limit
        if (!checkRateLimit()) {
            setSubmitStatus('rate_limit');
            setTimeout(() => setSubmitStatus(null), 5000);
            return;
        }

        setIsSubmitting(true);
        setSubmitStatus(null);

        try {
            const { serviceID, templateID, userID } = EMAILJS_CONFIG;

            // Sanitizar todos los inputs antes de enviar
            const sanitizedData = {
                from_name: sanitizeInput(formData.name, MAX_NAME_LENGTH),
                from_email: sanitizeInput(formData.email, MAX_EMAIL_LENGTH),
                message: sanitizeInput(formData.message, MAX_MESSAGE_LENGTH),
                to_name: 'Duo a Color',
                type: ' Contacto ',
                reply_to: sanitizeInput(formData.email, MAX_EMAIL_LENGTH),
                date: new Date().toLocaleString('es-ES', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                }),
                // Registro de consentimientos RGPD para aval legal
                privacy_accepted: 'SI',
                story_consent: formData.storyConsent ? 'SI' : 'NO',
                consent_info: `\n--- CONSENTIMIENTOS RGPD ---\nPolitica de Privacidad: ACEPTADA\nCompartir Historia: ${formData.storyConsent ? 'SI, AUTORIZADO' : 'NO AUTORIZADO'}`
            };

            await emailjs.send(serviceID, templateID, sanitizedData, userID);

            setSubmitStatus('success');
            setFormData({ name: '', email: '', message: '', privacyAccepted: false, storyConsent: false });
            setErrors({});

            // Trackear evento de formulario enviado en GA4
            events.contactFormSubmit();

            setTimeout(() => setSubmitStatus(null), 5000);

        } catch (error) {
            // Solo loguear errores en desarrollo
            if (process.env.NODE_ENV !== 'production') {
                console.error('Error sending email:', error);
            }
            setSubmitStatus('error');
            setTimeout(() => setSubmitStatus(null), 5000);
        } finally {
            setIsSubmitting(false);
        }
    };

    return {
        formData,
        handleChange,
        handleSubmit,
        isSubmitting,
        submitStatus,
        errors,
        // Exponer límites para uso en UI (ej: mostrar contador de caracteres)
        limits: {
            maxName: MAX_NAME_LENGTH,
            maxEmail: MAX_EMAIL_LENGTH,
            maxMessage: MAX_MESSAGE_LENGTH,
            minMessage: MIN_MESSAGE_LENGTH
        }
    };
};
